import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, Plus, Edit2, Search, TrendingUp, Eye, EyeOff, PackageX, RotateCcw, X } from 'lucide-react';
import type {
  CategoriaVista,
  InsumoVista,
  ProductoMetricaUsoVista,
  ProductoVista,
} from '@shared/types';
import { RetirarProductoModal } from '@renderer/components/productos/RetirarProductoModal';
import { RevisionInsumos } from '@renderer/components/productos/RevisionInsumos';
// Sprint 81 — Lo que se vende sin descontar inventario, y con qué unirlo.
import { SinInventarioAviso } from '@renderer/components/productos/SinInventarioAviso';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { VenderDelInventario } from '@renderer/components/productos/VenderDelInventario';
import { FormProducto } from '@renderer/components/forms/FormProducto';
// Sprint 82 — El alta que pidió el dueño: lo que vendo y sus formatos, en una captura.
import { AltaConFormatos } from '@renderer/components/productos/AltaConFormatos';
// Sprint 84 — Verlos agrupados como se capturaron, no sueltos como se guardan.
import { GruposDeVenta } from '@renderer/components/productos/GruposDeVenta';
import { ModalPorciones } from '@renderer/components/forms/ModalPorciones';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourProductos } from '@renderer/tours';
import { ETIQUETA_PRECIO_PENDIENTE, precioEstaPendiente } from '@shared/precioPendiente';

/**
 * ProductosPage — catálogo de productos.
 *
 * Sprint 9.3 — Mejoras UX tras retro:
 *   - Búsqueda por nombre (case-insensitive, sin acentos).
 *   - Columna "Insumo" humanizada: muestra nombre del insumo + gramaje
 *     legible en lugar de `× insumo #ID` (jergonista).
 *   - Columna nueva "Vendido este mes" con métrica de uso real.
 *   - Orden secundario por vendido descendente (los más usados arriba).
 */
export function ProductosPage() {
  // La revisión de ingredientes es de dueño: el handler la exige y la UI lo respeta.
  const esDueno = useSessionStore((st) => st.sesion?.rol) === 'dueño';
  const [params] = useSearchParams();
  const [productos, setProductos] = useState<ProductoVista[]>([]);
  const [categorias, setCategorias] = useState<CategoriaVista[]>([]);
  /**
   * Sprint 72 — "Vender del inventario".
   *
   * Nace de una pregunta del negocio: la dueña cargó 27 ingredientes y el sistema le pedía
   * capturar lo mismo otra vez, uno por uno. La mayoría se venden tal cual, así que aquí se
   * crean de un golpe: ella sólo pone el precio.
   */
  /**
   * Sprint 109 — Y desde este sprint se puede llegar aquí **desde el aviso de Inventario**, que es
   * donde el negocio se topa con el problema. La pantalla ya existía; lo que no existía era que
   * alguien la ofreciera sin tener que saber que está detrás de "Otras formas de agregar".
   */
  const [venderInventarioAbierto, setVenderInventarioAbierto] = useState(
    () => params.get('reparar') === 'invendibles',
  );
  const [insumos, setInsumos] = useState<InsumoVista[]>([]);
  const [metricas, setMetricas] = useState<ProductoMetricaUsoVista[]>([]);
  const [filtroCategoria, setFiltroCategoria] = useState<string>('todas');
  /**
   * Sprint 110 — Se puede llegar aquí **buscando algo concreto**.
   *
   * Existencias ya no edita el catálogo: su botón "Editar en Productos" manda aquí con el nombre
   * puesto. Sin esto, mandar a alguien a "Productos" desde una tarjeta concreta lo deja frente a
   * una lista de treinta y a buscar a mano lo que acababa de señalar con el dedo.
   */
  const [busqueda, setBusqueda] = useState(() => params.get('buscar') ?? '');
  const [editando, setEditando] = useState<ProductoVista | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(true);
  const negocio = useNegocioStore((s) => s.negocio);

  const cargar = async () => {
    setCargando(true);
    const [pRes, cRes, iRes, mRes] = await Promise.all([
      window.api.productos.listar(),
      window.api.categorias.listar(),
      window.api.insumos.listar(),
      window.api.productos.metricasUsoMes(),
    ]);
    if (pRes.ok) setProductos(pRes.data);
    if (cRes.ok) setCategorias(cRes.data);
    if (iRes.ok) setInsumos(iRes.data);
    if (mRes.ok) setMetricas(mRes.data);
    setCargando(false);
  };

  useEffect(() => {
    cargar();
  }, []);

  // Mapas indexados para lookups O(1) en la tabla.
  const insumoPorId = useMemo(() => {
    const m = new Map<number, InsumoVista>();
    for (const ins of insumos) m.set(ins.id, ins);
    return m;
  }, [insumos]);

  const usoPorProducto = useMemo(() => {
    const m = new Map<number, number>();
    for (const x of metricas) m.set(x.productoId, x.vendidoEnMes);
    return m;
  }, [metricas]);

  const norm = (s: string) =>
    s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const filtrados = useMemo(() => {
    const q = norm(busqueda.trim());
    let base = productos;
    if (filtroCategoria === 'sin') {
      base = base.filter((p) => !p.categoriaId);
    } else if (filtroCategoria !== 'todas') {
      const id = Number(filtroCategoria);
      base = base.filter((p) => p.categoriaId === id);
    }
    if (q.length > 0) {
      base = base.filter((p) => norm(p.nombre).includes(q));
    }
    // Orden secundario: los más vendidos del mes arriba dentro de la categoría.
    return [...base].sort((a, b) => {
      const ua = usoPorProducto.get(a.id) ?? 0;
      const ub = usoPorProducto.get(b.id) ?? 0;
      if (ua !== ub) return ub - ua;
      return a.nombre.localeCompare(b.nombre);
    });
  }, [productos, filtroCategoria, busqueda, usoPorProducto]);

  /**
   * Sprint 82 — "Nuevo producto" abre el alta con formatos.
   *
   * Antes abría el formulario clásico, que pide elegir un ingrediente **que ya tiene que existir**
   * — o sea, obligaba a capturar la misma cosa dos veces, primero en Inventario. El dueño lo dijo
   * sin rodeos: *"¿para qué putas volverlos a capturar?"*.
   *
   * El formulario clásico sigue vivo para **editar** y para el caso con receta de varios
   * ingredientes, que no es una fracción de nada.
   */
  const [altaAbierta, setAltaAbierta] = useState(false);
  /**
   * Sprint 84 — Cómo se listan: agrupados como se capturaron, o la tabla de siempre.
   *
   * Por defecto la agrupada, porque es el idioma del alta. La tabla sigue siendo mejor para
   * buscar entre cuarenta renglones y para ver costos, así que no se tira.
   */
  const [vista, setVista] = useState<'grupos' | 'tabla'>('grupos');
  /** Sprint 85 — Los caminos de apoyo, guardados detrás de un solo botón. */
  const [otrasFormas, setOtrasFormas] = useState(false);
  const onNuevo = () => setAltaAbierta(true);
  const onNuevoConReceta = () => {
    setEditando(null);
    setAbierto(true);
  };

  const onEditar = (p: ProductoVista) => {
    setEditando(p);
    setAbierto(true);
  };

  /**
   * Sprint 18 — Toggle Activo/Inactivo inline (sin abrir form).
   * El handler `productos.actualizar` acepta partial (Sprint 7+).
   */
  const onToggleHabilitado = async (p: ProductoVista) => {
    const res = await window.api.productos.actualizar(p.id, {
      habilitado: !p.habilitado,
    });
    if (res.ok) cargar();
    else window.alertar({ titulo: 'Error', mensaje: res.error, variant: 'warn' });
  };

  /**
   * Sprint 60 — "Dejar de vender" en lugar de "Eliminar".
   *
   * Eliminar no es una operación válida en un catálogo con historia: el handler
   * anterior sólo sabía fallar explicándole al dueño una restricción de la base
   * ("tiene 42 ventas históricas") cuando lo que él dijo fue "ya no vendo esto".
   * El modal resuelve además los paquetes que dependen del producto, que es donde
   * estaba el daño invisible.
   */
  const onRetirar = (p: ProductoVista) => setRetirando(p);

  /** Volver a venderlo: retirar no es borrar. */
  const onReactivar = async (p: ProductoVista) => {
    const res = await window.api.productos.reactivar(p.id);
    if (res.ok) cargar();
    else window.alertar({ titulo: 'Error', mensaje: res.error, variant: 'warn' });
  };

  const [retirando, setRetirando] = useState<ProductoVista | null>(null);
  /** Sprint 63 — Crear los tamaños de un ingrediente de un golpe. */
  const [porcionesAbierto, setPorcionesAbierto] = useState(false);
  const [avisoPorciones, setAvisoPorciones] = useState<string | null>(null);

  const onSaved = () => {
    setAbierto(false);
    cargar();
  };

  const nombreCategoria = (id?: number | null) =>
    categorias.find((c) => c.id === id)?.nombre ?? '—';

  // Sprint 9.3 — Mostrar la "carga del producto" en lenguaje natural.
  // Ej: "1 kg de Carne molida" en vez de "1 × insumo #4".
  const insumoLegible = (p: ProductoVista): string => {
    // Sprint 62 — Un guion no explicaba nada; ahora se dice qué significa.
    if (!p.insumoId) return 'no descuenta';
    const ins = insumoPorId.get(p.insumoId);
    if (!ins) return 'no descuenta';
    const cant = p.gramaje ?? 1;
    return `${cant} ${ins.unidad} de ${ins.nombre}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between" data-tour="prod-header">
        <div>
          <h1 className="text-2xl font-bold">Productos</h1>
          <p className="text-sm text-slate-500">Catálogo del menú.</p>
        </div>
        <div className="flex items-center gap-2">
          {/*
            Sprint 85 — UN camino visible para dar de alta, y los demás guardados.

            Reporte del dueño, señalando los cuatro botones: *"¿ahora qué pedo con esto?"*. Tenía
            razón: "Varios tamaños", "Vender del inventario", "Con receta" y "Nuevo producto"
            competían al mismo tamaño en la misma barra, y tres de ellos empiezan por el almacén —
            justo el orden que el Sprint 82 vino a eliminar.

            Lo dije en el 82 ("quedan en segundo plano") y no lo hice: los dejé del mismo tamaño y
            a la IZQUIERDA del principal, que es el lugar donde el ojo llega primero. Un botón que
            se anuncia como secundario y se ve como primario, es primario.

            Los tres siguen existiendo porque resuelven casos reales —un ingrediente que ya estaba
            ahí, un plato con varios ingredientes—, pero ahora hay que abrirlos: quien los necesita
            sabe que los busca; quien no, no tiene que elegir entre cuatro puertas para lo mismo.
          */}
          <div className="relative">
            <button
              onClick={() => setOtrasFormas((v) => !v)}
              className="btn-ghost min-h-[44px] border border-slate-300"
              aria-expanded={otrasFormas}
            >
              Otras formas de agregar
              <ChevronDown className={`h-4 w-4 transition ${otrasFormas ? 'rotate-180' : ''}`} />
            </button>

            {otrasFormas && (
              <div className="absolute right-0 z-20 mt-1 w-80 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
                <button
                  onClick={() => {
                    setOtrasFormas(false);
                    onNuevoConReceta();
                  }}
                  className="w-full rounded-lg px-3 py-2 text-left hover:bg-slate-50"
                >
                  <span className="block text-sm font-medium">Un platillo con varios ingredientes</span>
                  <span className="block text-xs text-slate-500">
                    Una torta: pan, carne y queso. No es un tamaño de nada.
                  </span>
                </button>
                <button
                  onClick={() => {
                    setOtrasFormas(false);
                    setPorcionesAbierto(true);
                  }}
                  disabled={insumos.length === 0}
                  className="w-full rounded-lg px-3 py-2 text-left hover:bg-slate-50 disabled:opacity-40"
                >
                  <span className="block text-sm font-medium">
                    Tamaños de algo que ya está en Inventario
                  </span>
                  <span className="block text-xs text-slate-500">
                    Cuando el ingrediente se capturó antes y todavía no se vende.
                  </span>
                </button>
                <button
                  onClick={() => {
                    setOtrasFormas(false);
                    setVenderInventarioAbierto(true);
                  }}
                  disabled={insumos.length === 0}
                  className="w-full rounded-lg px-3 py-2 text-left hover:bg-slate-50 disabled:opacity-40"
                >
                  <span className="block text-sm font-medium">Vender algo del inventario tal cual</span>
                  <span className="block text-xs text-slate-500">
                    Se entrega completo, sin partirlo: una lata, un pan.
                  </span>
                </button>
              </div>
            )}
          </div>

          <button onClick={onNuevo} className="btn-primary min-h-[44px]" data-tour="prod-nuevo">
            <Plus className="h-4 w-4" />
            Nuevo producto
          </button>
          <HelpButton tour={tourProductos} />
        </div>
      </div>

      {/*
        Sprint 60 — Revisión de ingredientes (DT-038). Va arriba porque es un
        chequeo del catálogo completo, no del listado: cuando no hay nada que
        revisar se limita a decirlo en una línea.
      */}
      {/*
        Sprint 81 — Va ANTES de la revisión de vínculos: "no tiene inventario" es más grave y más
        frecuente que "el vínculo se ve raro", y fue lo que dejó a la dueña sin entender por qué su
        inventario en verde no aparecía en el POS.
      */}
      {esDueno && <SinInventarioAviso />}
      {esDueno && <RevisionInsumos />}

      {/* Sprint 9.3 — Búsqueda + filtro */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center" data-tour="prod-filtros">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            className="input pl-9"
            placeholder="Buscar por nombre…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            aria-label="Buscar producto"
          />
        </div>
        <select
          className="input max-w-xs"
          value={filtroCategoria}
          onChange={(e) => setFiltroCategoria(e.target.value)}
        >
          <option value="todas">Todas las categorías</option>
          <option value="sin">Sin categoría</option>
          {categorias.map((c) => (
            <option key={c.id} value={String(c.id)}>{c.nombre}</option>
          ))}
        </select>
      </div>

      {avisoPorciones && (
        <div className="flex items-start justify-between gap-3 rounded-lg border-2 border-emerald-200 bg-emerald-50 px-4 py-3">
          <p className="text-sm font-medium text-emerald-900">{avisoPorciones}</p>
          <button
            onClick={() => setAvisoPorciones(null)}
            className="text-emerald-700 hover:text-emerald-900"
            aria-label="Cerrar aviso"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/*
        Sprint 84 — Dos formas de ver lo mismo, y la que manda es la del negocio.

        Reporte del dueño: dio de alta "Frijoles Charros · 1 lt y 1/2 lt" como una cosa y la lista
        se lo devolvió en dos renglones sueltos, sin ningún lugar donde agregarle el 3/4 —"¿y ahora
        dónde quedó Frijoles Charros donde se veían agrupados?"—.

        "Como los vendo" es ahora la vista por defecto: es el idioma en que se capturó. La tabla
        completa se conserva porque sigue siendo la mejor para buscar un renglón entre cuarenta,
        revisar costos o ver de un vistazo qué descuenta cada cosa.
      */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setVista('grupos')}
          className={`min-h-[44px] rounded-lg border-2 px-4 text-sm font-medium ${
            vista === 'grupos'
              ? 'border-primary bg-blue-50 text-blue-900'
              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          Como los vendo
        </button>
        <button
          onClick={() => setVista('tabla')}
          className={`min-h-[44px] rounded-lg border-2 px-4 text-sm font-medium ${
            vista === 'tabla'
              ? 'border-primary bg-blue-50 text-blue-900'
              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          Tabla completa
        </button>
      </div>

      {vista === 'grupos' && (
        <GruposDeVenta categorias={categorias} onCambio={() => void cargar()} busquedaInicial={params.get('buscar') ?? ''} />
      )}

      <div
        className={`card overflow-hidden p-0 ${vista === 'grupos' ? 'hidden' : ''}`}
        data-tour="prod-tabla"
      >
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Categoría</th>
              <th className="px-4 py-3 text-right">Precio</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3">Descuenta de</th>
              <th className="px-4 py-3 text-right">Mes</th>
              <th className="px-4 py-3 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {cargando ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  Cargando…
                </td>
              </tr>
            ) : filtrados.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  {busqueda || filtroCategoria !== 'todas' ? (
                    'No se encontraron productos con ese filtro.'
                  ) : (
                    /* Sprint 62 — Una pantalla vacía tiene que decir qué sigue. */
                    <span className="text-slate-600">
                      Todavía no hay nada que vender.{' '}
                      <a href="#/primeros-pasos" className="font-medium text-primary underline">
                        Ver los primeros pasos
                      </a>{' '}
                      o usa el botón <strong>Nuevo producto</strong>.
                    </span>
                  )}
                </td>
              </tr>
            ) : (
              filtrados.map((p) => {
                const uso = usoPorProducto.get(p.id) ?? 0;
                /**
                 * Sprint 62 — `null` = no lleva control de inventario. Antes caía a
                 * `inventarioActual` (0) y la tabla marcaba AGOTADO en rojo cada
                 * producto sin ingrediente ligado, que además no se podía vender.
                 */
                const stock = p.stockEfectivo ?? null;
                const stockMin = p.stockMinimoEfectivo ?? null;
                const sinControl = stock === null;
                const agotado = stock !== null && stock <= 0;
                const critico = !agotado && stock !== null && stockMin !== null && stockMin > 0 && stock <= stockMin;
                const bajo =
                  !agotado && !critico && stock !== null && stockMin !== null && stockMin > 0 && stock <= stockMin * 2;
                const stockColor = sinControl
                  ? 'bg-slate-100 text-slate-500'
                  : agotado
                  ? 'bg-slate-200 text-slate-700'
                  : critico
                  ? 'bg-red-100 text-red-700'
                  : bajo
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-emerald-100 text-emerald-700';
                return (
                  <tr key={p.id} className={`hover:bg-slate-50 ${!p.habilitado ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{p.nombre}</div>
                      {/* Sprint 62 — Se quitó `tipo`: nadie lo leía y decía "corte" de un refresco. */}
                      <div className="text-xs uppercase tracking-wide text-slate-600">
                        {p.unidadVenta ?? 'pieza'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{nombreCategoria(p.categoriaId)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-mono font-semibold">
                        {formatPrecio(p.precio, negocio?.monedaSimbolo ?? '$', negocio?.locale ?? 'es-MX')}
                      </div>
                      {/*
                        Sprint 111 — El producto capturado "con un peso mientras le pongo el precio".

                        Se puede vender en el mostrador —ahí el operador ve el precio antes de
                        cobrar— pero **no sale en el enlace público**, y eso hay que decirlo donde
                        se captura, no descubrirlo cuando un cliente reclame que no aparece.
                      */}
                      {precioEstaPendiente(p.precio) && (
                        <div
                          className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800"
                          title="Mientras no tenga precio real, no se muestra en el menú que se le manda al cliente."
                        >
                          ⚠️ {ETIQUETA_PRECIO_PENDIENTE}
                        </div>
                      )}
                      {p.precioEnPaquete != null && p.precioEnPaquete !== p.precio && (
                        <div className="text-[10px] text-slate-400 font-mono">
                          en paq:{' '}
                          {formatPrecio(p.precioEnPaquete, negocio?.monedaSimbolo ?? '$', negocio?.locale ?? 'es-MX')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={`inline-block rounded px-2 py-0.5 font-mono text-xs font-semibold ${stockColor}`}
                        title={sinControl ? 'Este producto no descuenta de ningún ingrediente' : undefined}
                      >
                        {sinControl ? 'sin control' : agotado ? 'AGOTADO' : stock}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{insumoLegible(p)}</td>
                    <td className="px-4 py-3 text-right">
                      {uso > 0 ? (
                        <span className="inline-flex items-center gap-1 font-mono font-semibold text-emerald-700">
                          <TrendingUp className="h-3.5 w-3.5" />
                          {uso}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => onToggleHabilitado(p)}
                          className={`flex items-center justify-center rounded h-10 w-10 transition ${
                            p.habilitado
                              ? 'text-amber-700 hover:bg-amber-50'
                              : 'text-emerald-700 hover:bg-emerald-50'
                          }`}
                          title={p.habilitado ? 'Ocultar del menú POS' : 'Mostrar en menú POS'}
                          aria-label={p.habilitado ? 'Ocultar' : 'Mostrar'}
                        >
                          {p.habilitado ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                        </button>
                        <button
                          onClick={() => onEditar(p)}
                          className="flex items-center justify-center rounded h-10 w-10 text-slate-700 hover:bg-slate-100 transition"
                          aria-label="Editar"
                          title="Editar producto"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        {p.habilitado ? (
                          <button
                            onClick={() => onRetirar(p)}
                            className="flex items-center justify-center rounded h-10 w-10 text-amber-700 hover:bg-amber-50 transition"
                            aria-label="Dejar de vender"
                            title="Dejar de vender (conserva el historial)"
                          >
                            <PackageX className="h-5 w-5" />
                          </button>
                        ) : (
                          <button
                            onClick={() => onReactivar(p)}
                            className="flex items-center justify-center rounded h-10 w-10 text-green-700 hover:bg-green-50 transition"
                            aria-label="Volver a vender"
                            title="Volver a vender"
                          >
                            <RotateCcw className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ModalPorciones

        abierto={porcionesAbierto}

        insumos={insumos.filter((i) => i.activo)}

        categorias={categorias}

        onClose={() => setPorcionesAbierto(false)}

        onCreadas={(cuantas) => {

          setPorcionesAbierto(false);

          setAvisoPorciones(

            `Se ${cuantas === 1 ? 'creó 1 producto' : `crearon ${cuantas} productos`}. Ya se pueden vender.`,

          );

          void cargar();

        }}

      />


      {venderInventarioAbierto && (


        <VenderDelInventario


          categorias={categorias}


          onCerrar={() => setVenderInventarioAbierto(false)}


          onCreados={(cuantos) => {


            setVenderInventarioAbierto(false);


            void cargar();


            window.alertar({


              titulo: cuantos === 1 ? 'Producto creado' : `${cuantos} productos creados`,


              mensaje:


                'Ya están en el punto de venta y descuentan del inventario. ' +


                'Revisa los precios antes de vender.',


              variant: 'success',


            });


          }}


        />


      )}



      <AltaConFormatos
        abierto={altaAbierta}
        categorias={categorias}
        onCerrar={() => setAltaAbierta(false)}
        onCreado={() => void cargar()}
      />

      <FormProducto
        abierto={abierto}
        producto={editando}
        categorias={categorias}
        onClose={() => setAbierto(false)}
        onSaved={onSaved}
      />

      {retirando && (
        <RetirarProductoModal
          productoId={retirando.id}
          productoNombre={retirando.nombre}
          onCerrar={() => setRetirando(null)}
          onListo={() => {
            setRetirando(null);
            cargar();
          }}
        />
      )}
    </div>
  );
}
